/**
 * DriverActivityDashboard.jsx
 * Dashboard de Atividade dos Motoristas - Fleet Premium
 *
 * Exibe em tempo real:
 * - Estatisticas de motoristas (total, ativos, em rota, offline)
 * - Mapa com posicoes dos motoristas
 * - Lista de motoristas com status e progresso de rota
 * - Filtros por status e busca por nome
 */

import {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Badge,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Person as DriverIcon,
  AccessTime as TimeIcon,
  RadioButtonChecked as OnlineIcon,
  RadioButtonUnchecked as OfflineIcon,
  DirectionsCar as OnRouteIcon,
  Pause as PausedIcon,
  Map as MapIcon,
  ViewList as ListIcon,
  MyLocation as LocationIcon,
  Route as RouteIcon,
  Phone as PhoneIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

import maplibregl from 'maplibre-gl';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useEffectAsync } from '../reactHelper';
import { useTranslation } from '../common/components/LocalizationProvider';
import MapView from '../map/core/MapView';

import { getRoutePlans, PLAN_STATUS } from '../common/services/routePlanService';

// ============================================================================
// Helper Functions
// ============================================================================

const getDriverStatus = (driver, activeRoute) => {
  if (!driver) return 'unknown';

  // Check if driver has an active route
  if (activeRoute) {
    if (activeRoute.status === PLAN_STATUS.EXECUTING) {
      return 'on_route';
    }
    if (activeRoute.status === 'paused') {
      return 'paused';
    }
  }

  // Check last activity
  const lastLogin = driver.lastLogin ? new Date(driver.lastLogin) : null;
  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

  if (lastLogin && lastLogin > fiveMinutesAgo) {
    return 'online';
  }

  return 'offline';
};

const getStatusColor = (status, tokens) => {
  switch (status) {
    case 'on_route':
      return tokens?.colors?.status?.success || '#10b981';
    case 'online':
      return tokens?.colors?.status?.info || '#3b82f6';
    case 'paused':
      return tokens?.colors?.status?.warning || '#f59e0b';
    case 'offline':
    default:
      return tokens?.colors?.text?.muted || '#9ca3af';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'on_route':
      return t('driverStatusOnRoute') || 'Em Rota';
    case 'online':
      return t('driverStatusOnline') || 'Online';
    case 'paused':
      return t('driverStatusPaused') || 'Pausado';
    case 'offline':
    default:
      return t('driverStatusOffline') || 'Offline';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'on_route':
      return <OnRouteIcon />;
    case 'online':
      return <OnlineIcon />;
    case 'paused':
      return <PausedIcon />;
    case 'offline':
    default:
      return <OfflineIcon />;
  }
};

const formatLastSeen = (lastLogin, t) => {
  if (!lastLogin) return t('sharedNever') || 'Nunca';

  const date = new Date(lastLogin);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('sharedJustNow') || 'Agora';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  return `${diffDays}d`;
};

// ============================================================================
// Sub-components
// ============================================================================

const StatsCard = ({
  icon: Icon, label, value, color, tokens, onClick, active,
}) => (
  <Card
    onClick={onClick}
    sx={{
      flex: 1,
      minWidth: 120,
      cursor: onClick ? 'pointer' : 'default',
      backgroundColor: active ? `${color}15` : tokens?.colors?.surface?.raised,
      border: `1px solid ${active ? color : tokens?.colors?.border?.subtle}`,
      transition: 'all 0.2s ease',
      '&:hover': onClick ? {
        transform: 'translateY(-2px)',
        boxShadow: `0 4px 12px ${color}30`,
      } : {},
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: `${color}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color, fontSize: 22 }} />
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{ color: tokens?.colors?.text?.primary, fontWeight: 700, lineHeight: 1 }}
          >
            {value}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: tokens?.colors?.text?.muted, fontSize: '11px' }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DriverCard = ({
  driver, status, activeRoute, tokens, t, onViewRoute, onViewLocation,
}) => {
  const statusColor = getStatusColor(status, tokens);
  const progress = activeRoute?.progress || 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 1.5,
        backgroundColor: tokens?.colors?.surface?.raised,
        border: `1px solid ${tokens?.colors?.border?.subtle}`,
        borderRadius: tokens?.radius?.lg || '12px',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: statusColor,
          boxShadow: `0 2px 8px ${statusColor}20`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Avatar */}
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={(
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: statusColor,
                border: `2px solid ${tokens?.colors?.surface?.raised}`,
              }}
            />
          )}
        >
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: `${statusColor}20`,
              color: statusColor,
            }}
          >
            {driver.name?.[0]?.toUpperCase() || <DriverIcon />}
          </Avatar>
        </Badge>

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: tokens?.colors?.text?.primary,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {driver.name}
            </Typography>
            <Chip
              size="small"
              icon={getStatusIcon(status)}
              label={getStatusLabel(status, t)}
              sx={{
                height: 22,
                fontSize: '11px',
                backgroundColor: `${statusColor}15`,
                color: statusColor,
                '& .MuiChip-icon': { color: statusColor, fontSize: 14 },
              }}
            />
          </Box>

          {driver.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 14, color: tokens?.colors?.text?.muted }} />
              <Typography variant="caption" sx={{ color: tokens?.colors?.text?.muted }}>
                {driver.phone}
              </Typography>
            </Box>
          )}

          {/* Active Route Info */}
          {activeRoute && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <RouteIcon sx={{ fontSize: 14, color: tokens?.colors?.text?.muted }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: tokens?.colors?.text?.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {activeRoute.name || `Rota #${activeRoute.id}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: `${statusColor}20`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: statusColor,
                      borderRadius: 3,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: statusColor, fontWeight: 600, minWidth: 40 }}
                >
                  {Math.round(progress)}%
                </Typography>
              </Box>
            </Box>
          )}

          {/* Last seen (for offline drivers) */}
          {status === 'offline' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <TimeIcon sx={{ fontSize: 14, color: tokens?.colors?.text?.muted }} />
              <Typography variant="caption" sx={{ color: tokens?.colors?.text?.muted }}>
                {t('driverLastSeen') || 'Visto'}: {formatLastSeen(driver.lastLogin, t)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {driver.lastLatitude && driver.lastLongitude && (
            <Tooltip title={t('commandDeviceLocation') || 'Ver localizacao'}>
              <IconButton
                size="small"
                onClick={() => onViewLocation?.(driver)}
                sx={{ color: tokens?.colors?.text?.muted }}
              >
                <LocationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {activeRoute && (
            <Tooltip title={t('sharedRoute') || 'Ver rota'}>
              <IconButton
                size="small"
                onClick={() => onViewRoute?.(activeRoute)}
                sx={{ color: tokens?.colors?.text?.muted }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const DriverListSkeleton = ({ tokens }) => (
  <Box>
    {[1, 2, 3, 4].map((i) => (
      <Paper
        key={i}
        elevation={0}
        sx={{
          p: 2,
          mb: 1.5,
          backgroundColor: tokens?.colors?.surface?.raised,
          border: `1px solid ${tokens?.colors?.border?.subtle}`,
          borderRadius: tokens?.radius?.lg || '12px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        </Box>
      </Paper>
    ))}
  </Box>
);

// ============================================================================
// Main Component
// ============================================================================

const DriverActivityDashboard = () => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [routePlans, setRoutePlans] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [mapInstance, _setMapInstance] = useState(null);
  const [markers, setMarkers] = useState([]);

  // Get drivers from Redux
  const _driversFromStore = useSelector((state) => Object.values(state.drivers.items || {}));

  // Load data
  useEffectAsync(async () => {
    setLoading(true);
    try {
      // Load drivers
      const driversResponse = await fetch('/api/drivers');
      if (driversResponse.ok) {
        const driversData = await driversResponse.json();
        setDrivers(driversData);
      }

      // Load active route plans
      const plans = await getRoutePlans({
        status: PLAN_STATUS.EXECUTING,
      });
      setRoutePlans(plans);
    } catch (error) {
      console.error('Failed to load driver data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const driversResponse = await fetch('/api/drivers');
        if (driversResponse.ok) {
          const driversData = await driversResponse.json();
          setDrivers(driversData);
        }

        const plans = await getRoutePlans({
          status: PLAN_STATUS.EXECUTING,
        });
        setRoutePlans(plans);
      } catch (error) {
        console.error('Refresh failed:', error);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Build driver data with status and active route
  const driversWithStatus = useMemo(() => drivers.map((driver) => {
    // Find active route for this driver
    const activeRoute = routePlans.find((plan) => {
      const vehicles = plan.vehicles || [];
      return vehicles.some((v) => v.driverId === driver.id);
    });

    // Calculate progress if there's an active route
    let progress = 0;
    if (activeRoute) {
      const stops = activeRoute.stops || [];
      const completedStops = stops.filter((s) => s.status === 'completed' || s.status === 'skipped').length;
      progress = stops.length > 0 ? (completedStops / stops.length) * 100 : 0;
    }

    const status = getDriverStatus(driver, activeRoute);

    return {
      ...driver,
      status,
      activeRoute: activeRoute ? { ...activeRoute, progress } : null,
    };
  }), [drivers, routePlans]);

  // Filter drivers
  const filteredDrivers = useMemo(() => {
    let result = driversWithStatus;

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter((d) => d.status === statusFilter);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((d) => d.name?.toLowerCase().includes(query)
          || d.phone?.includes(query)
          || d.cpf?.includes(query));
    }

    return result;
  }, [driversWithStatus, statusFilter, searchQuery]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = driversWithStatus.length;
    const onRoute = driversWithStatus.filter((d) => d.status === 'on_route').length;
    const online = driversWithStatus.filter((d) => d.status === 'online').length;
    const paused = driversWithStatus.filter((d) => d.status === 'paused').length;
    const offline = driversWithStatus.filter((d) => d.status === 'offline').length;

    return {
      total, onRoute, online, paused, offline,
    };
  }, [driversWithStatus]);

  // Update map markers
  useEffect(() => {
    if (!mapInstance || viewMode !== 'map') return;

    // Clear existing markers
    markers.forEach((m) => m.remove());

    // Add new markers
    const newMarkers = filteredDrivers
      .filter((d) => d.lastLatitude && d.lastLongitude)
      .map((driver) => {
        const statusColor = getStatusColor(driver.status, tokens);

        // Create marker element
        const el = document.createElement('div');
        el.className = 'driver-marker';
        el.innerHTML = `
          <div style="
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: ${statusColor};
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 14px;
          ">
            ${driver.name?.[0]?.toUpperCase() || 'D'}
          </div>
        `;

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([driver.lastLongitude, driver.lastLatitude])
          .setPopup(
            new maplibregl.Popup({ offset: 25 }).setHTML(`
              <strong>${driver.name}</strong><br/>
              ${getStatusLabel(driver.status, t)}<br/>
              ${driver.activeRoute ? `Rota: ${driver.activeRoute.name || '#' + driver.activeRoute.id}` : ''}
            `),
          )
          .addTo(mapInstance);

        return marker;
      });

    setMarkers(newMarkers);

    // Fit bounds if markers exist
    if (newMarkers.length > 0) {
      const bounds = new maplibregl.LngLatBounds();
      filteredDrivers
        .filter((d) => d.lastLatitude && d.lastLongitude)
        .forEach((d) => bounds.extend([d.lastLongitude, d.lastLatitude]));

      mapInstance.fitBounds(bounds, { padding: 50, maxZoom: 14 });
    }

    return () => {
      newMarkers.forEach((m) => m.remove());
    };
  }, [mapInstance, filteredDrivers, viewMode, tokens, t]);

  // Handlers
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      const driversResponse = await fetch('/api/drivers');
      if (driversResponse.ok) {
        const driversData = await driversResponse.json();
        setDrivers(driversData);
      }

      const plans = await getRoutePlans({
        status: PLAN_STATUS.EXECUTING,
      });
      setRoutePlans(plans);
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleViewRoute = useCallback((route) => {
    navigate(`/routing/execution/${route.id}`);
  }, [navigate]);

  const handleViewLocation = useCallback((driver) => {
    if (mapInstance && driver.lastLatitude && driver.lastLongitude) {
      setViewMode('map');
      setTimeout(() => {
        mapInstance.flyTo({
          center: [driver.lastLongitude, driver.lastLatitude],
          zoom: 16,
        });
      }, 100);
    }
  }, [mapInstance]);

  const _handleStatusFilter = useCallback((_, newStatus) => {
    if (newStatus !== null) {
      setStatusFilter(newStatus);
    }
  }, []);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens?.colors?.surface?.default,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${tokens?.colors?.border?.subtle}`,
          backgroundColor: tokens?.colors?.surface?.raised,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate(-1)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
            {t('driverActivityDashboard') || 'Atividade dos Motoristas'}
          </Typography>
          <Tooltip title={t('sharedRefresh') || 'Atualizar'}>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StatsCard
            icon={DriverIcon}
            label={t('sharedTotal') || 'Total'}
            value={stats.total}
            color={tokens?.colors?.text?.primary || '#374151'}
            tokens={tokens}
            onClick={() => setStatusFilter('all')}
            active={statusFilter === 'all'}
          />
          <StatsCard
            icon={OnRouteIcon}
            label={t('driverStatusOnRoute') || 'Em Rota'}
            value={stats.onRoute}
            color="#10b981"
            tokens={tokens}
            onClick={() => setStatusFilter('on_route')}
            active={statusFilter === 'on_route'}
          />
          <StatsCard
            icon={OnlineIcon}
            label={t('driverStatusOnline') || 'Online'}
            value={stats.online}
            color="#3b82f6"
            tokens={tokens}
            onClick={() => setStatusFilter('online')}
            active={statusFilter === 'online'}
          />
          <StatsCard
            icon={PausedIcon}
            label={t('driverStatusPaused') || 'Pausado'}
            value={stats.paused}
            color="#f59e0b"
            tokens={tokens}
            onClick={() => setStatusFilter('paused')}
            active={statusFilter === 'paused'}
          />
          <StatsCard
            icon={OfflineIcon}
            label={t('driverStatusOffline') || 'Offline'}
            value={stats.offline}
            color="#9ca3af"
            tokens={tokens}
            onClick={() => setStatusFilter('offline')}
            active={statusFilter === 'offline'}
          />
        </Box>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: `1px solid ${tokens?.colors?.border?.subtle}`,
        }}
      >
        <TextField
          size="small"
          placeholder={t('sharedSearch') || 'Buscar...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1, maxWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: tokens?.colors?.text?.muted }} />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="list">
            <ListIcon />
          </ToggleButton>
          <ToggleButton value="map">
            <MapIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {viewMode === 'list' ? (
          <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            {loading ? (
              <DriverListSkeleton tokens={tokens} />
            ) : filteredDrivers.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: tokens?.colors?.text?.muted,
                }}
              >
                <DriverIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6">
                  {t('sharedNoData') || 'Nenhum motorista encontrado'}
                </Typography>
              </Box>
            ) : (
              filteredDrivers.map((driver) => (
                <DriverCard
                  key={driver.id}
                  driver={driver}
                  status={driver.status}
                  activeRoute={driver.activeRoute}
                  tokens={tokens}
                  t={t}
                  onViewRoute={handleViewRoute}
                  onViewLocation={handleViewLocation}
                />
              ))
            )}
          </Box>
        ) : (
          <MapView>
            <Box
              ref={(ref) => {
                if (ref && !mapInstance) {
                  // Map will be available through MapView context
                }
              }}
              sx={{ width: '100%', height: '100%' }}
            />
          </MapView>
        )}
      </Box>

      {/* CSS for marker animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default DriverActivityDashboard;
